import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { State } from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { TokenStorageService } from '@core/services/token-storage.service';
import {
  navigateAfterLogin,
  navigateAfterUserAuth,
} from '@core/utils/auth-routing.util';
import type { ExchangeTicketApiResponse } from '@core/api/workspace/models';
import {
  CreateCompanyRequestBillingCycle,
  CreateCompanyRequestPlanKey,
  ExchangeTicketDataResponsePurpose,
} from '@core/api/workspace/models';
import type { CreateCompanyRequestBillingCycle as BillingCycle } from '@core/api/workspace/models/createCompanyRequestBillingCycle';
import type { CreateCompanyRequestPlanKey as PlanKey } from '@core/api/workspace/models/createCompanyRequestPlanKey';

const VALID_PLAN_KEYS = Object.values(CreateCompanyRequestPlanKey);
const VALID_BILLING_CYCLES = Object.values(CreateCompanyRequestBillingCycle);

@Component({
  selector: 'app-google-success-page',
  imports: [RouterLink],
  templateUrl: './google-success-page.html',
  styleUrl: './google-success-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoogleSuccessPage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly tokenStorage = inject(TokenStorageService);

  readonly authState = new State<ExchangeTicketApiResponse>();
  readonly currentYear = new Date().getFullYear();

  ngOnInit(): void {
    const ticket = this.route.snapshot.queryParamMap.get('ticket');

    if (!ticket) {
      this.router.navigate(['/login']);
      return;
    }

    this.executeRequest({
      state: this.authState,
      request: this.userAPIService.exchangeTicket({ ticket }),
      showLoader: true,
      handleError: true,
      errorMessage: 'Google sign-in failed',
      onSuccess: (response) => {
        void this.navigateAfterAuth(response);
      },
      onFailed: () => {
        this.router.navigate(['/auth/google/error'], {
          queryParams: { code: 'exchange_failed' },
        });
      },
    });
  }

  private async navigateAfterAuth(response: ExchangeTicketApiResponse): Promise<void> {
    const { purpose, workspace, context } = response.data;

    if (purpose === ExchangeTicketDataResponsePurpose.ACCEPT_INVITATION) {
      this.tokenStorage.setSession(response.data);
      await navigateAfterLogin(this.router, {
        workspace,
        returnUrl: '/dashboard',
      });
      return;
    }

    if (purpose === ExchangeTicketDataResponsePurpose.SIGNUP && response.data.isNewUser) {
      this.tokenStorage.setUserSession(response.data);

      const planKey = this.resolvePlanKey(context?.planKey);
      const billingCycle = this.resolveBillingCycle(context?.billingCycle);

      await this.router.navigate(['/create-company'], {
        queryParams: { planKey, billingCycle },
      });
      return;
    }

    this.tokenStorage.setUserSession(response.data);
    await navigateAfterUserAuth(this.router, this.userAPIService, this.tokenStorage, {
      workspace: response.data.workspace,
      workspaceAccessCount: response.data.workspaceAccessCount,
      workspaceIds: response.data.workspaceIds,
      returnUrl: context?.returnUrl,
    });
  }

  private resolvePlanKey(value?: string): PlanKey {
    const normalized = value?.trim().toLowerCase();
    return VALID_PLAN_KEYS.includes(normalized as PlanKey)
      ? (normalized as PlanKey)
      : CreateCompanyRequestPlanKey.free;
  }

  private resolveBillingCycle(value?: string): BillingCycle {
    const normalized = value?.trim().toUpperCase();
    return VALID_BILLING_CYCLES.includes(normalized as BillingCycle)
      ? (normalized as BillingCycle)
      : CreateCompanyRequestBillingCycle.MONTHLY;
  }
}
